import { useState } from 'react'
import { X, Mail, CheckCircle } from 'lucide-react'
import { useSendEmailVerificationMutation, useVerifyEmailMutation } from '../store/api/apiSlice'
import { useAppDispatch, useAppSelector } from '../hooks/useAppStore'
import { setUser, selectCurrentUser } from '../store/slices/authSlice'

interface EmailVerificationModalProps {
    isOpen: boolean
    onClose: () => void
    userEmail: string
}

export default function EmailVerificationModal({ isOpen, onClose, userEmail }: EmailVerificationModalProps) {
    const dispatch = useAppDispatch()
    const currentUser = useAppSelector(selectCurrentUser)
    const [sendCode, { isLoading: isSending }] = useSendEmailVerificationMutation()
    const [verifyCode, { isLoading: isVerifying }] = useVerifyEmailMutation()

    const [code, setCode] = useState(['', '', '', '', '', ''])
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [codeSent, setCodeSent] = useState(false)

    const handleSendCode = async () => {
        setError('')
        try {
            await sendCode().unwrap()
            setCodeSent(true)
        } catch (err: any) {
            setError(err?.data?.message || 'Failed to send verification code')
        }
    }

    const handleCodeChange = (index: number, value: string) => {
        if (value.length > 1) {
            value = value.slice(0, 1)
        }

        if (!/^\d*$/.test(value)) return

        const newCode = [...code]
        newCode[index] = value
        setCode(newCode)

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`code-${index + 1}`)
            nextInput?.focus()
        }
    }

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            const prevInput = document.getElementById(`code-${index - 1}`)
            prevInput?.focus()
        }
    }

    const handleVerify = async () => {
        const fullCode = code.join('')
        if (fullCode.length !== 6) {
            setError('Please enter the complete 6-digit code')
            return
        }

        setError('')
        try {
            await verifyCode({ code: fullCode }).unwrap()
            setSuccess(true)

            // Update the Redux user state to reflect email verification
            if (currentUser) {
                dispatch(setUser({
                    ...currentUser,
                    isEmailVerified: true
                }))
            }

            // Close modal after showing success message
            setTimeout(() => {
                onClose()
            }, 2000)
        } catch (err: any) {
            setError(err?.data?.message || 'Invalid verification code')
        }
    }

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
        const newCode = pastedData.split('')
        while (newCode.length < 6) newCode.push('')
        setCode(newCode)

        // Focus the last filled input or the next empty one
        const nextEmptyIndex = newCode.findIndex(c => !c)
        const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex
        document.getElementById(`code-${focusIndex}`)?.focus()
    }

    if (!isOpen) return null

    if (success) {
        return (
            <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
                <div className='bg-white rounded-2xl p-8 max-w-md w-full text-center'>
                    <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                        <CheckCircle className='w-8 h-8 text-green-600' />
                    </div>
                    <h2 className='text-2xl font-bold text-gray-900 mb-2'>Email Verified!</h2>
                    <p className='text-gray-600'>Your email has been successfully verified.</p>
                </div>
            </div>
        )
    }

    return (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-2xl p-6 md:p-8 max-w-md w-full relative'>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className='absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors'
                >
                    <X className='w-5 h-5 text-gray-500' />
                </button>

                {/* Header */}
                <div className='text-center mb-6'>
                    <div className='w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                        <Mail className='w-8 h-8 text-[#FF6B35]' />
                    </div>
                    <h2 className='text-2xl font-bold text-gray-900 mb-2'>Verify Your Email</h2>
                    <p className='text-gray-600 text-sm'>
                        {codeSent
                            ? `We've sent a 6-digit code to ${userEmail}`
                            : `Click the button below to receive a verification code at ${userEmail}`
                        }
                    </p>
                </div>

                {!codeSent ? (
                    // Send Code Button
                    <button
                        onClick={handleSendCode}
                        disabled={isSending}
                        className='w-full bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white py-3 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        {isSending ? 'Sending...' : 'Send Verification Code'}
                    </button>
                ) : (
                    // Code Input
                    <>
                        <div className='mb-6'>
                            <div className='flex gap-2 justify-center mb-4' onPaste={handlePaste}>
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        id={`code-${index}`}
                                        type='text'
                                        inputMode='numeric'
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className='w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-[#FF6B35] focus:outline-none transition-colors'
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
                                    <p className='text-red-600 text-sm text-center'>{error}</p>
                                </div>
                            )}

                            {/* Resend Code */}
                            <div className='text-center'>
                                <button
                                    onClick={handleSendCode}
                                    disabled={isSending}
                                    className='text-sm text-[#FF6B35] hover:underline disabled:opacity-50'
                                >
                                    Resend Code
                                </button>
                            </div>
                        </div>

                        {/* Verify Button */}
                        <button
                            onClick={handleVerify}
                            disabled={isVerifying || code.some(d => !d)}
                            className='w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {isVerifying ? 'Verifying...' : 'Verify Email'}
                        </button>
                    </>
                )}

                {/* Info Text */}
                <p className='text-xs text-gray-500 text-center mt-4'>
                    Code expires in 10 minutes
                </p>
            </div>
        </div>
    )
}
